import React, { useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import {
  Activity,
  AlertTriangle,
  BarChart3,
  Bell,
  Car,
  CheckCircle2,
  Clock3,
  Database,
  DollarSign,
  FileCheck,
  History,
  Image as ImageIcon,
  LayoutDashboard,
  LifeBuoy,
  Lock,
  LogOut,
  Mail,
  MapPin,
  Menu,
  Package,
  Palette,
  RadioTower,
  RefreshCw,
  Search,
  Send,
  Settings,
  ShieldCheck,
  Sparkles,
  Trash2,
  UserRound,
  Users,
  Utensils,
  Wrench,
  X,
  XCircle
} from 'lucide-react';
import { API } from '../../context/AuthContext';
import { useAuth } from '../../context/AuthContext';
import {
  DEFAULT_BRANDING_VISUAL,
  applyFavicon,
  readBrandingVisual,
  saveBrandingVisual
} from '../../utils/brandingVisualConfig';
import '../../components/admin/AdminCommandCenter.css';
import AdminApprovals from './AdminApprovals';
import AdminEmailCenter from './AdminEmailCenter';
import AdminClients from './AdminClients';
import AdminVisualEditorLauncher from './AdminVisualEditorLauncher';

const roleLabels = {
  superadmin: 'Superadmin',
  operaciones: 'Operaciones',
  finanzas: 'Finanzas',
  soporte: 'Soporte',
  marketing: 'Marketing'
};

const pageMeta = {
  dashboard: ['Dashboard Ejecutivo', 'Resumen operativo, financiero y de riesgos de PROVI'],
  operaciones: ['Operaciones', 'Taxi, Delivery y Comida en una sola zona operativa'],
  proveedores: ['Proveedores', 'Taxistas, repartidores y restaurantes'],
  clientes: ['Clientes', 'Historial, bloqueos, actividad y soporte de usuarios'],
  aprobaciones: ['Aprobaciones', 'Verificaciones, documentos y solicitudes pendientes'],
  finanzas: ['Finanzas', 'Comisiones, ingresos, liquidaciones y exportaciones'],
  soporte: ['Soporte', 'Reclamos, tickets, reembolsos y atención al usuario'],
  reportes: ['Reportes', 'Análisis ejecutivo del crecimiento y rendimiento'],
  contenido: ['Contenido', 'Landing, marca, promociones y apariencia pública'],
  notificaciones: ['Centro de correos', 'Plantillas, marca, pruebas, historial y auditoría de correos'],
  sistema: ['Sistema', 'Configuración, permisos, auditoría, versiones y diagnóstico']
};

const rolePages = {
  superadmin: ['dashboard', 'operaciones', 'proveedores', 'clientes', 'aprobaciones', 'finanzas', 'soporte', 'reportes', 'contenido', 'notificaciones', 'sistema'],
  operaciones: ['dashboard', 'operaciones', 'proveedores', 'clientes', 'aprobaciones', 'soporte', 'reportes'],
  finanzas: ['dashboard', 'proveedores', 'finanzas', 'reportes'],
  soporte: ['dashboard', 'operaciones', 'clientes', 'soporte'],
  marketing: ['dashboard', 'reportes', 'contenido']
};

const navGroups = [
  {
    title: 'Principal',
    items: [
      { id: 'dashboard', label: 'Dashboard', icon: LayoutDashboard },
      { id: 'operaciones', label: 'Operaciones', icon: RadioTower, count: 3 },
      { id: 'proveedores', label: 'Proveedores', icon: Users },
      { id: 'clientes', label: 'Clientes', icon: UserRound },
      { id: 'aprobaciones', label: 'Aprobaciones', icon: ShieldCheck }
    ]
  },
  {
    title: 'Negocio',
    items: [
      { id: 'finanzas', label: 'Finanzas', icon: DollarSign },
      { id: 'soporte', label: 'Soporte', icon: LifeBuoy },
      { id: 'reportes', label: 'Reportes', icon: BarChart3 }
    ]
  },
  {
    title: 'Gestión',
    items: [
      { id: 'contenido', label: 'Contenido', icon: Palette },
      { id: 'notificaciones', label: 'Notificaciones', icon: Mail },
      { id: 'sistema', label: 'Sistema', icon: Settings }
    ]
  }
];

const emailTemplateDefaults = [
  { type: 'otp_verification', name: 'Código de verificación', active: true },
  { type: 'welcome_client', name: 'Bienvenida cliente', active: true },
  { type: 'password_reset', name: 'Recuperación de contraseña', active: true },
  { type: 'password_changed', name: 'Contraseña actualizada', active: true },
  { type: 'login_alert', name: 'Alerta de inicio de sesión', active: true },
  { type: 'welcome_provider', name: 'Bienvenida proveedor', active: true },
  { type: 'admin_account_invitation', name: 'Cuenta administrador', active: true },
  { type: 'approval_approved', name: 'Solicitud aprobada', active: true },
  { type: 'approval_rejected', name: 'Solicitud rechazada', active: true }
];

const brandingVisualAssets = [
  {
    key: 'logo_main_url',
    name: 'Logo principal',
    usage: 'Landing, pantallas principales, tarjetas grandes.',
    recommended: '512x512 o SVG',
    format: 'PNG, SVG',
    preview: 'logo'
  },
  {
    key: 'logo_horizontal_url',
    name: 'Logo horizontal',
    usage: 'Navbar, footer, encabezados.',
    recommended: '1200x300 · Relación 4:1',
    format: 'PNG, SVG',
    preview: 'logo'
  },
  {
    key: 'logo_icon_url',
    name: 'Ícono cuadrado',
    usage: 'Botones, cards, avatar de marca.',
    recommended: '512x512 · Relación 1:1',
    format: 'PNG, SVG',
    preview: 'icon'
  },
  {
    key: 'favicon_url',
    name: 'Favicon / ícono de pestaña',
    usage: 'Pestaña del navegador.',
    recommended: '32x32, 48x48 o 64x64 · Relación 1:1',
    format: 'ICO, PNG, SVG',
    preview: 'favicon'
  },
  {
    key: 'pwa_icon_192_url',
    name: 'Ícono PWA 192x192',
    usage: 'App instalada en Android/PWA.',
    recommended: '192x192 · Relación 1:1',
    format: 'PNG',
    preview: 'icon'
  },
  {
    key: 'pwa_icon_512_url',
    name: 'Ícono PWA 512x512',
    usage: 'App instalada, splash/iconos de alta resolución.',
    recommended: '512x512 · Relación 1:1',
    format: 'PNG',
    preview: 'icon'
  },
  {
    key: 'auth_logo_url',
    name: 'Logo para login y registro',
    usage: 'Pantallas de inicio de sesión y registro.',
    recommended: '512x512 o SVG',
    format: 'PNG, SVG',
    preview: 'icon'
  },
  {
    key: 'admin_logo_url',
    name: 'Logo para panel admin',
    usage: 'Panel administrador.',
    recommended: '512x512 o SVG',
    format: 'PNG, SVG',
    preview: 'icon'
  }
];

const fallbackProviders = [
  { id: 'taxi-demo', name: 'Taxi Demo Pedro', provider_type: 'taxi', status: 'Activo', visibility: 'Visible', expires: '+30d', services: 0, rating: 'Sin datos', commission: '$0.00' },
  { id: 'delivery-demo', name: 'Delivery Demo Ana', provider_type: 'delivery', status: 'Activo', visibility: 'Visible', expires: '+30d', services: 0, rating: 'Sin datos', commission: '$0.00' },
  { id: 'restaurant-demo', name: 'Restaurant Demo Gourmet', provider_type: 'local', status: 'Revisión', visibility: 'Visible', expires: '+30d', services: 0, rating: 'Sin datos', commission: '$0.00' }
];


const cn = (...items) => items.filter(Boolean).join(' ');


const LEGACY_FONT_SCALE = {
  small: 92,
  normal: 100,
  large: 112
};

const clampFontScale = (
  value,
  legacyValue = 'normal'
) => {
  const parsed = Number(value);

  if (Number.isFinite(parsed)) {
    return Math.min(
      115,
      Math.max(90, Math.round(parsed))
    );
  }

  return (
    LEGACY_FONT_SCALE[legacyValue]
    || LEGACY_FONT_SCALE.normal
  );
};

const normalizeUiPreferences = (
  source = {},
  defaultSidebarMode = 'expanded'
) => ({
  font_scale: clampFontScale(
    source.font_scale,
    source.font_size
  ),
  sidebar_mode:
    source.sidebar_mode === 'compact'
      || source.sidebar_mode === 'expanded'
      ? source.sidebar_mode
      : defaultSidebarMode
});

const Badge = ({ children, tone = 'blue' }) => <span className={`provi-badge ${tone}`}>{children}</span>;

const Button = ({ children, tone = '', className = '', ...props }) => (
  <button {...props} className={cn('provi-btn', tone, className)}>{children}</button>
);

const MiniButton = ({ children, tone = '', ...props }) => (
  <button {...props} className={cn('provi-mini-btn', tone)}>{children}</button>
);

const IconBox = ({ children }) => <div className="provi-icon-box">{children}</div>;

const KpiCard = ({ icon: Icon, label, value, sub, tone }) => (
  <article className="provi-kpi" data-testid="dashboard-stats-card">
    <div className="provi-kpi-top">
      <IconBox><Icon size={22} color={tone || '#ff1493'} /></IconBox>
      <span className="provi-kpi-label">{label}</span>
    </div>
    <div className="provi-kpi-number">{value}</div>
    <div className="provi-kpi-sub">{sub}</div>
  </article>
);

const Panel = ({ title, subtitle, action, children }) => (
  <section className="provi-panel">
    <div className="provi-panel-head">
      <div>
        <div className="provi-panel-title">{title}</div>
        {subtitle && <div className="provi-panel-subtitle">{subtitle}</div>}
      </div>
      {action}
    </div>
    {children}
  </section>
);

const RowCard = ({ icon: Icon, title, subtitle, badge, badgeTone = 'blue', meta = [], actions }) => (
  <div className="provi-row-card">
    <div className="provi-row-main">
      <IconBox><Icon size={20} /></IconBox>
      <div className="provi-row-text">
        <strong>{title}</strong>
        <span>{subtitle}</span>
      </div>
      {badge && <Badge tone={badgeTone}>{badge}</Badge>}
    </div>
    {meta.length > 0 && (
      <div className="provi-meta-grid">
        {meta.map((item) => (
          <div className="provi-meta" key={item.label}>
            <span>{item.label}</span>
            <strong>{item.value}</strong>
          </div>
        ))}
      </div>
    )}
    {actions && <div className="provi-actions">{actions}</div>}
  </div>
);

const AdminCommandCenter = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [activePage, setActivePage] = useState('dashboard');
  const [contentTab, setContentTab] = useState('editor');
  const [editorDirty, setEditorDirty] = useState(false);
  const [pendingEditorAction, setPendingEditorAction] = useState(null);
  const [roleView, setRoleView] = useState('superadmin');
  const [stats, setStats] = useState({ providers: 0, commissions: 0, alerts: 0, pending: 0 });
  const [providers, setProviders] = useState(fallbackProviders);
  const [alerts, setAlerts] = useState([]);
  const [adminRequests, setAdminRequests] = useState([]);
  const [approvalPendingCount, setApprovalPendingCount] = useState(0);
  const [emailTemplates, setEmailTemplates] = useState(emailTemplateDefaults);
  const [branding, setBranding] = useState({
    company_name: 'PROVI',
    owner_name: 'PROVI',
    support_email: 'soporte@provi.app',
    logo_url: '',
    show_navbar: true,
    show_footer: true,
    show_login: true,
    show_register: true,
    show_emails: true,
    show_admin_dashboard: true,
    show_provider_dashboard: true,
    show_client_dashboard: true
  });
  const [brandingVisual, setBrandingVisual] = useState(() => readBrandingVisual());
  const [search, setSearch] = useState('');
  const [toast, setToast] = useState('');
  const [confirm, setConfirm] = useState(null);
  const [modal, setModal] = useState(null);
  const [audit, setAudit] = useState([
    { type: 'Sistema', text: 'Panel administrador PROVI iniciado', at: 'ahora' }
  ]);
  const [showAudit, setShowAudit] = useState(false);
  const defaultSidebarMode = typeof window !== 'undefined' && window.innerWidth >= 1181 ? 'expanded' : 'compact';
  const [uiPreferences, setUiPreferences] = useState({ font_scale: 100, sidebar_mode: defaultSidebarMode });
  const [preferenceDraft, setPreferenceDraft] = useState({ font_scale: 100, sidebar_mode: defaultSidebarMode });
  const [savingPreferences, setSavingPreferences] = useState(false);
  const [showUiPreferences, setShowUiPreferences] = useState(false);
  const [temporarySidebarOpen, setTemporarySidebarOpen] = useState(false);
  const [sidebarHovering, setSidebarHovering] = useState(false);
  const [sidebarFocusWithin, setSidebarFocusWithin] = useState(false);
  const closeTimerRef = useRef(null);
  const savingPreferencesRef = useRef(false);

  const allowedPages = rolePages[roleView] || rolePages.superadmin;
  const visibleProviders = providers.length ? providers : fallbackProviders;
  const visibleTemplates = emailTemplates.length ? emailTemplates : emailTemplateDefaults;

  const currentTitle = pageMeta[activePage] || pageMeta.dashboard;

  const addAudit = (type, text) => {
    setAudit((prev) => [{ type, text, at: new Date().toLocaleString() }, ...prev].slice(0, 30));
  };

  const showToast = (message) => {
    setToast(message);
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => setToast(''), 2400);
  };

  const updateBrandingVisualAsset = (key, value) => {
    setBrandingVisual((prev) => ({ ...prev, [key]: value }));
  };

  const saveBrandingVisualSettings = (nextConfig = brandingVisual) => {
    const savedConfig = saveBrandingVisual(nextConfig);
    setBrandingVisual(savedConfig);
    applyFavicon(savedConfig.favicon_url);
    window.dispatchEvent(new Event('provi:branding-visual-updated'));
    showToast('Identidad visual guardada');
    addAudit('Contenido', 'Actualizó identidad visual / logos');
  };

  const resetBrandingVisualAsset = (key) => {
    const nextConfig = { ...brandingVisual, [key]: DEFAULT_BRANDING_VISUAL[key] || '' };
    saveBrandingVisualSettings(nextConfig);
  };

  const hasUnsavedEditorChanges = (
    activePage === 'contenido'
    && contentTab === 'editor'
    && editorDirty
  );

  const runWithEditorGuard = (action) => {
    if (hasUnsavedEditorChanges) {
      setPendingEditorAction({ run: action });
      return false;
    }

    action();
    return true;
  };

  const navigateToPage = (page) => {
    if (page === 'contenido') {
      setContentTab('editor');
    }

    setActivePage(page);
    setTemporarySidebarOpen(false);
    addAudit('Navegación', `Entró a ${pageMeta[page]?.[0] || page}`);
  };

  const goTo = (page) => {
    if (!allowedPages.includes(page)) {
      showToast('Este rol no tiene permiso para esa sección.');
      return;
    }

    if (
      page === 'contenido'
      && activePage === 'contenido'
      && contentTab === 'editor'
    ) {
      setContentTab('editor');
      return;
    }

    runWithEditorGuard(() => navigateToPage(page));
  };

  const askConfirm = (title, text, action, result = 'Acción registrada') => {
    setConfirm({
      title,
      text,
      action: () => {
        if (action) action();
        showToast(result);
        addAudit('Acción crítica', `${title}: ${result}`);
        setConfirm(null);
      }
    });
  };

  const performLogout = async () => {
    try {
      await logout();
    } finally {
      navigate('/login');
    }
  };

  const handleLogout = () => {
    runWithEditorGuard(performLogout);
  };

  const loadData = async () => {
    try {
      const [statsRes, providersRes, alertsRes, adminReqRes, templatesRes, brandingRes] = await Promise.allSettled([
        axios.get(`${API}/admin/stats`, { withCredentials: true }),
        axios.get(`${API}/providers?approved=true`, { withCredentials: true }),
        axios.get(`${API}/admin/alerts?read=false`, { withCredentials: true }),
        axios.get(`${API}/admin/approvals`, { withCredentials: true }),
        axios.get(`${API}/admin/email-center/templates`, { withCredentials: true }),
        axios.get(`${API}/admin/branding`, { withCredentials: true })
      ]);

      if (statsRes.status === 'fulfilled') {
        const data = statsRes.value.data || {};
        setStats({
          providers: data.total_providers ?? data.providers ?? visibleProviders.length,
          commissions: data.total_commissions ?? data.commissions ?? 0,
          alerts: data.alerts ?? 0,
          pending: data.pending ?? 0
        });
      }
      if (providersRes.status === 'fulfilled' && Array.isArray(providersRes.value.data)) {
        setProviders(providersRes.value.data.map((item, index) => ({
          id: item.id || item._id || `provider-${index}`,
          name: item.name || item.full_name || item.email || 'Proveedor',
          provider_type: item.provider_type || item.type || 'taxi',
          status: item.active === false ? 'Suspendido' : item.approved === false ? 'Revisión' : 'Activo',
          visibility: item.visible === false ? 'Oculto' : 'Visible',
          expires: item.visibility_expires_at || '+30d',
          services: item.services_count || 0,
          rating: item.rating || 'Sin datos',
          commission: item.commission || '$0.00'
        })));
      }
      if (alertsRes.status === 'fulfilled') setAlerts(alertsRes.value.data || []);
      if (adminReqRes.status === 'fulfilled') {
        setAdminRequests(adminReqRes.value.data?.items || []);
        setApprovalPendingCount(adminReqRes.value.data?.pending_count || 0);
      }
      if (templatesRes.status === 'fulfilled') setEmailTemplates(templatesRes.value.data?.items || emailTemplateDefaults);
      if (brandingRes.status === 'fulfilled') setBranding((prev) => ({ ...prev, ...(brandingRes.value.data || {}) }));
      addAudit('Sistema', 'Datos del administrador actualizados');
    } catch (error) {
      console.error('Error cargando admin:', error);
    }
  };

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const userId = user?.id || user?._id;

    if (!userId) return;

    const fallbackSidebarMode =
      window.innerWidth >= 1181
        ? 'expanded'
        : 'compact';

    const cacheKey =
      `provi:admin-ui-preferences:${userId}`;

    let cached = null;

    try {
      cached = JSON.parse(
        window.localStorage.getItem(cacheKey)
      );
    } catch (error) {
      window.localStorage.removeItem(
        cacheKey
      );
    }

    if (cached) {
      const normalizedCached =
        normalizeUiPreferences(
          cached,
          fallbackSidebarMode
        );

      setUiPreferences(normalizedCached);
      setPreferenceDraft(normalizedCached);
    }

    axios.get(
      `${API}/auth/ui-preferences`,
      { withCredentials: true }
    )
      .then(({ data }) => {
        const resolved =
          normalizeUiPreferences(
            {
              ...data,
              sidebar_mode: data.is_saved
                ? data.sidebar_mode
                : fallbackSidebarMode
            },
            fallbackSidebarMode
          );

        setUiPreferences(resolved);
        setPreferenceDraft(resolved);

        window.localStorage.setItem(
          cacheKey,
          JSON.stringify(resolved)
        );
      })
      .catch(() => {
        if (!cached) {
          const fallback = {
            font_scale: 100,
            sidebar_mode:
              fallbackSidebarMode
          };

          setUiPreferences(fallback);
          setPreferenceDraft(fallback);
        }
      });
  }, [user?.id, user?._id]);

  const saveUiPreferences = async () => {
    if (savingPreferencesRef.current) {
      return false;
    }

    savingPreferencesRef.current = true;
    setSavingPreferences(true);

    try {
      const payload = {
        font_scale: clampFontScale(
          preferenceDraft.font_scale
        ),
        sidebar_mode:
          preferenceDraft.sidebar_mode
      };

      const { data } = await axios.put(
        `${API}/auth/ui-preferences`,
        payload,
        { withCredentials: true }
      );

      const saved = normalizeUiPreferences(
        data,
        defaultSidebarMode
      );

      setUiPreferences(saved);
      setPreferenceDraft(saved);

      const userId =
        user?.id || user?._id;

      if (userId) {
        window.localStorage.setItem(
          `provi:admin-ui-preferences:${userId}`,
          JSON.stringify(saved)
        );
      }

      showToast('Preferencias guardadas');
      return true;
    } catch (error) {
      showToast(
        'No se pudieron guardar las preferencias'
      );
      return false;
    } finally {
      savingPreferencesRef.current = false;
      setSavingPreferences(false);
    }
  };

  const criticalModalOpen = Boolean(
    modal
    || confirm
    || showAudit
    || showUiPreferences
    || pendingEditorAction
  );
  const sidebarInteracting = sidebarHovering || sidebarFocusWithin;

  useEffect(() => {
    window.clearTimeout(closeTimerRef.current);
    if (!temporarySidebarOpen || sidebarInteracting || criticalModalOpen) return undefined;
    const attemptClose = () => {
      if (document.querySelector('[role="dialog"], .provi-modal-backdrop.show')) {
        closeTimerRef.current = window.setTimeout(attemptClose, 8000);
        return;
      }
      setTemporarySidebarOpen(false);
    };
    closeTimerRef.current = window.setTimeout(attemptClose, 8000);
    return () => window.clearTimeout(closeTimerRef.current);
  }, [temporarySidebarOpen, sidebarInteracting, criticalModalOpen]);

  useEffect(() => {
    const handleEscape = (event) => {
      const nestedDialogOpen = document.querySelector('[role="dialog"], .provi-modal-backdrop.show');
      if (event.key === 'Escape' && temporarySidebarOpen && !criticalModalOpen && !nestedDialogOpen) setTemporarySidebarOpen(false);
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [temporarySidebarOpen, criticalModalOpen]);

  const toggleSidebar = () => {
    const isMobile = window.innerWidth <= 640;

    if (isMobile) {
      setTemporarySidebarOpen((current) => !current);
      return;
    }

    setTemporarySidebarOpen(false);

    const nextMode =
      uiPreferences.sidebar_mode === 'expanded'
        ? 'compact'
        : 'expanded';

    setUiPreferences((current) => ({
      ...current,
      sidebar_mode: nextMode
    }));

    setPreferenceDraft((current) => ({
      ...current,
      sidebar_mode: nextMode
    }));
  };

  useEffect(() => {
    const handleViewportResize = () => {
      if (window.innerWidth > 640) {
        setTemporarySidebarOpen(false);
      }
    };

    window.addEventListener(
      'resize',
      handleViewportResize
    );

    return () => {
      window.removeEventListener(
        'resize',
        handleViewportResize
      );
    };
  }, []);

  useEffect(() => {
    if (!allowedPages.includes(activePage)) {
      setActivePage('dashboard');
    }
  }, [allowedPages, activePage]);

  const kpis = useMemo(() => [
    { icon: Users, label: 'Proveedores', value: stats.providers || visibleProviders.length, sub: `${visibleProviders.filter((p) => p.status === 'Activo').length} activos · ${visibleProviders.filter((p) => p.status !== 'Activo').length} en revisión`, tone: '#ff1493' },
    { icon: FileCheck, label: 'Aprobaciones', value: approvalPendingCount, sub: 'Documentos y solicitudes pendientes', tone: '#9d28ff' },
    { icon: DollarSign, label: 'Comisiones', value: `$${Number(stats.commissions || 0).toFixed(2)}`, sub: 'Hoy $0.00 · Mes en progreso', tone: '#00e5ff' },
    { icon: AlertTriangle, label: 'Alertas críticas', value: alerts.length || stats.alerts || 0, sub: 'Pagos, reclamos o errores activos', tone: '#facc15' }
  ], [stats, visibleProviders, adminRequests.length, approvalPendingCount, alerts.length]);

  const renderKpis = () => (
    <div className="provi-kpi-grid">
      {kpis.map((item) => <KpiCard key={item.label} {...item} />)}
    </div>
  );

  const renderDashboard = () => (
    <>
      {renderKpis()}
      <div className="provi-grid-2">
        <Panel title="📌 Tareas de hoy" subtitle="Lo que el administrador debe resolver primero." action={<Button tone="primary" onClick={loadData}><RefreshCw size={16} />Actualizar</Button>}>
          <div className="provi-list">
            <RowCard icon={ShieldCheck} title="Aprobaciones" subtitle="Documentos y solicitudes por revisar." badge={approvalPendingCount} badgeTone="green" />
            <RowCard icon={LifeBuoy} title="Soporte" subtitle="Tickets abiertos y reclamos activos." badge="0" badgeTone="green" />
            <RowCard icon={DollarSign} title="Liquidaciones" subtitle="Pagos pendientes a proveedores." badge="$0.00" badgeTone="blue" />
          </div>
        </Panel>
        <Panel title="🚨 Alertas por prioridad" subtitle="Solo problemas reales, no ruido decorativo.">
          <div className="provi-list">
            <RowCard icon={AlertTriangle} title="Críticas" subtitle="Pagos fallidos, seguridad o caída del sistema." badge="0" badgeTone="green" />
            <RowCard icon={Clock3} title="Medias" subtitle="Documentos vencidos o reclamos moderados." badge="0" badgeTone="green" />
            <RowCard icon={Bell} title="Informativas" subtitle="Actividad normal del sistema." badge="3" badgeTone="blue" />
          </div>
        </Panel>
      </div>
      <Panel title="🚦 Operación rápida" subtitle="Taxi, Delivery y Comida agrupados en una sola zona operativa." action={<Button tone="primary" onClick={() => goTo('operaciones')}>Ver operaciones</Button>}>
        <div className="provi-grid-3">
          <ModuleCard icon={Car} title="Taxi" status="Activo" text="Solicitudes, ofertas, contraofertas, viajes activos, PIN y tracking." actions={<><MiniButton onClick={() => setModal({ title: 'Solicitudes Taxi', text: 'Aquí van solicitudes y ofertas pendientes.' })}>Solicitudes</MiniButton><MiniButton onClick={() => setModal({ title: 'Historial Taxi', text: 'Viajes completados, cancelados y rechazados.' })}>Historial</MiniButton></>} />
          <ModuleCard icon={Utensils} title="Comida" status="Activo" text="Restaurantes, menús, productos, horarios, pedidos y comisiones." actions={<><MiniButton onClick={() => setModal({ title: 'Restaurantes', text: 'Administrar restaurantes, horarios y menús.' })}>Restaurantes</MiniButton><MiniButton onClick={() => setModal({ title: 'Pedidos', text: 'Pedidos activos, entregados y cancelados.' })}>Pedidos</MiniButton></>} />
          <ModuleCard icon={Package} title="Delivery" status="Próximo" statusTone="yellow" text="Repartidores, zonas de entrega, tarifas y estados del servicio." actions={<MiniButton onClick={() => setModal({ title: 'Zonas Delivery', text: 'Configurar radio, zonas y tarifa base.' })}>Zonas</MiniButton>} />
        </div>
      </Panel>
    </>
  );

  const renderOperaciones = () => (
    <>
      <div className="provi-kpi-grid">
        <KpiCard icon={Car} label="Viajes activos" value="0" sub="Taxi en tiempo real" tone="#ff1493" />
        <KpiCard icon={Utensils} label="Pedidos comida" value="0" sub="Restaurantes y menús" tone="#00e5ff" />
        <KpiCard icon={Package} label="Delivery" value="0" sub="Módulo en preparación" tone="#9d28ff" />
        <KpiCard icon={XCircle} label="Cancelaciones" value="0" sub="Últimas 24 horas" tone="#facc15" />
      </div>
      <Panel title="🚕📦🍔 Operaciones por módulo" subtitle="Viajes, pedidos, cancelaciones, estados y seguimiento.">
        <div className="provi-grid-3">
          <ModuleCard icon={Car} title="Taxi" status="Activo" text="Solicitudes nuevas, ofertas pendientes, viajes aceptados, tracking, PIN y cancelaciones." actions={<><MiniButton>Solicitudes</MiniButton><MiniButton>Activos</MiniButton><MiniButton>Historial</MiniButton></>} />
          <ModuleCard icon={Utensils} title="Comida" status="Activo" text="Restaurantes, productos, menús, pedidos, horarios y disponibilidad." actions={<><MiniButton>Restaurantes</MiniButton><MiniButton>Menús</MiniButton></>} />
          <ModuleCard icon={Package} title="Delivery" status="Preparación" statusTone="yellow" text="Repartidores, solicitudes de envío, zonas de entrega y tarifas." actions={<><MiniButton>Zonas</MiniButton><MiniButton>Preparar</MiniButton></>} />
        </div>
      </Panel>
    </>
  );

  const renderProveedores = () => (
    <Panel title="👥 Proveedores" subtitle="Gestión operativa de taxistas, repartidores y restaurantes." action={<Button tone="primary" onClick={() => setModal({ title: 'Nuevo proveedor', text: 'Formulario preparado para registrar taxista, repartidor o restaurante.' })}>Nuevo proveedor</Button>}>
      <div className="provi-filterbar">
        <input className="provi-input" style={{ maxWidth: 340 }} placeholder="Buscar proveedor..." value={search} onChange={(e) => setSearch(e.target.value)} />
        <Button onClick={() => showToast('Exportando proveedores...')}>Exportar Excel</Button>
      </div>
      <div className="provi-table-wrap">
        <table className="provi-table" data-testid="providers-mgmt-content">
          <thead><tr><th>Proveedor</th><th>Tipo</th><th>Estado</th><th>Visibilidad</th><th>Servicios</th><th>Calificación</th><th>Comisiones</th><th>Acciones</th></tr></thead>
          <tbody>
            {visibleProviders.filter((p) => `${p.name} ${p.provider_type}`.toLowerCase().includes(search.toLowerCase())).map((provider) => (
              <tr key={provider.id}>
                <td data-label="Proveedor"><div className="provi-name-cell"><div className="provi-avatar">{provider.name?.[0] || 'P'}</div><div><strong>{provider.name}</strong><br /><span className="provi-muted">{provider.expires}</span></div></div></td>
                <td data-label="Tipo">{provider.provider_type}</td>
                <td data-label="Estado"><Badge tone={provider.status === 'Activo' ? 'green' : 'yellow'}>{provider.status}</Badge></td>
                <td data-label="Visibilidad"><Badge tone="purple">{provider.visibility}</Badge></td>
                <td data-label="Servicios">{provider.services}</td>
                <td data-label="Calificación">{provider.rating}</td>
                <td data-label="Comisiones">{provider.commission}</td>
                <td data-label="Acciones"><div className="provi-actions"><MiniButton onClick={() => setModal({ title: 'Perfil proveedor', text: 'Detalle, documentos, historial y rendimiento.' })}>Ver</MiniButton><MiniButton tone="success" onClick={() => askConfirm('Extender visibilidad', 'Se extenderá la visibilidad 30 días.', null, 'Visibilidad extendida +30d')}>+30d</MiniButton><MiniButton tone="danger" onClick={() => askConfirm('Suspender proveedor', 'El proveedor dejará de operar temporalmente. ¿Continuar?', null, 'Proveedor suspendido')}>Suspender</MiniButton></div></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Panel>
  );

  const renderClientes = () => (
    <AdminClients onToast={showToast} />
  );

  const renderAprobaciones = () => <AdminApprovals onCountChange={setApprovalPendingCount} onToast={showToast} />;

  const renderFinanzas = () => (
    <>
      <div className="provi-kpi-grid">
        <KpiCard icon={DollarSign} label="Ingresos hoy" value="$0.00" sub="Total generado hoy" tone="#00e5ff" />
        <KpiCard icon={DollarSign} label="Comisiones mes" value="$0.00" sub="Taxi, delivery y comida" tone="#ff1493" />
        <KpiCard icon={FileCheck} label="Por liquidar" value="$0.00" sub="Pagos a proveedores" tone="#9d28ff" />
        <KpiCard icon={Send} label="Reportes" value="0" sub="Exportaciones generadas" tone="#facc15" />
      </div>
      <div className="provi-grid-2">
        <Panel title="💰 Comisiones por módulo" subtitle="Taxi, delivery y comida separados.">
          <div className="provi-list">
            <RowCard icon={Car} title="Taxi" subtitle="Hoy: $0.00 · Mes: $0.00" badge="$0.00" badgeTone="blue" />
            <RowCard icon={Utensils} title="Comida" subtitle="Hoy: $0.00 · Mes: $0.00" badge="$0.00" badgeTone="blue" />
            <RowCard icon={Package} title="Delivery" subtitle="Hoy: $0.00 · Mes: $0.00" badge="$0.00" badgeTone="blue" />
          </div>
        </Panel>
        <Panel title="🧾 Liquidaciones" subtitle="Pagos pendientes a proveedores." action={<Button tone="primary" onClick={() => setModal({ title: 'Nueva liquidación', text: 'Generar liquidación manual a proveedor.' })}>Nueva</Button>}>
          <div className="provi-empty">No hay liquidaciones pendientes. Silencio financiero, sospechosamente pacífico.</div>
        </Panel>
      </div>
    </>
  );

  const renderSoporte = () => (
    <>
      <Panel title="🎫 Soporte y reclamos" subtitle="Reclamos, tickets, reembolsos y atención al usuario." action={<Button tone="primary" onClick={() => setModal({ title: 'Nuevo ticket', text: 'Crear ticket manual para cliente o proveedor.' })}>Nuevo ticket</Button>}>
        <div className="provi-grid-3">
          <ModuleCard icon={Car} title="Reclamos Taxi" status="0" statusTone="green" text="Viajes no completados, cancelaciones, trato, seguridad y cobros." actions={<MiniButton>Ver casos</MiniButton>} />
          <ModuleCard icon={Utensils} title="Reclamos Comida" status="0" statusTone="green" text="Pedidos incompletos, retrasos, calidad y reembolsos." actions={<MiniButton>Ver casos</MiniButton>} />
          <ModuleCard icon={Package} title="Reclamos Delivery" status="0" statusTone="green" text="Envíos perdidos, demoras y entregas fallidas." actions={<MiniButton>Ver casos</MiniButton>} />
        </div>
      </Panel>
      <Panel title="📋 Flujo de ticket" subtitle="Estados reales de atención.">
        <div className="provi-list">
          <RowCard icon={Clock3} title="Abierto" subtitle="Ticket nuevo sin revisión." badge="0" badgeTone="blue" />
          <RowCard icon={Search} title="En revisión" subtitle="Soporte está analizando el caso." badge="0" badgeTone="yellow" />
          <RowCard icon={Mail} title="Esperando respuesta" subtitle="Falta respuesta de cliente o proveedor." badge="0" badgeTone="yellow" />
          <RowCard icon={CheckCircle2} title="Resuelto" subtitle="Caso cerrado correctamente." badge="0" badgeTone="green" />
        </div>
      </Panel>
    </>
  );

  const renderReportes = () => (
    <div className="provi-grid-2">
      <Panel title="📈 Reportes ejecutivos" subtitle="Crecimiento, zonas, horas pico, cancelaciones y proveedores rentables." action={<Button onClick={() => showToast('Reporte exportado')}>Exportar</Button>}>
        <div className="provi-chart" />
      </Panel>
      <Panel title="📍 Indicadores clave" subtitle="Datos para tomar decisiones.">
        <div className="provi-list">
          <RowCard icon={MapPin} title="Zona más activa" subtitle="Sin datos suficientes." badge="N/A" badgeTone="blue" />
          <RowCard icon={Clock3} title="Hora pico" subtitle="Sin datos suficientes." badge="N/A" badgeTone="blue" />
          <RowCard icon={XCircle} title="Tasa de cancelación" subtitle="Últimos 30 días." badge="0%" badgeTone="green" />
        </div>
      </Panel>
    </div>
  );

  const renderBrandingVisualAssets = () => (
    <div className="provi-branding-assets">
      {brandingVisualAssets.map((asset) => {
        const value = brandingVisual[asset.key] || '';
        const previewClass = asset.preview === 'favicon' ? 'favicon' : asset.preview === 'logo' ? 'logo' : 'icon';

        return (
          <article key={asset.key} className="provi-branding-asset-card">
            <div className="provi-branding-asset-head">
              <div>
                <strong>{asset.name}</strong>
                <span>{asset.usage}</span>
              </div>
              <div className={`provi-branding-preview ${previewClass}`}>
                {value ? <img src={value} alt={asset.name} /> : <span>Por defecto</span>}
              </div>
            </div>
            <div className="provi-branding-specs">
              <span>{asset.recommended}</span>
              <span>{asset.format}</span>
            </div>
            <input
              className="provi-input"
              value={value}
              onChange={(event) => updateBrandingVisualAsset(asset.key, event.target.value)}
              placeholder="https://..."
              type="url"
            />
            <div className="provi-actions compact">
              <MiniButton onClick={() => saveBrandingVisualSettings()}>Guardar</MiniButton>
              <MiniButton tone="danger" onClick={() => resetBrandingVisualAsset(asset.key)}>Restaurar por defecto</MiniButton>
            </div>
          </article>
        );
      })}
    </div>
  );

  const renderContenido = () => (
    <section className="provi-content-studio" data-testid="admin-content-studio">
      <div className="provi-content-tabs" role="tablist" aria-label="Herramientas de contenido">
        <button
          type="button"
          role="tab"
          aria-selected={contentTab === 'editor'}
          className={contentTab === 'editor' ? 'active' : ''}
          onClick={() => setContentTab('editor')}
        >
          Editor visual
        </button>
        <button
          type="button"
          role="tab"
          aria-selected={contentTab === 'brand'}
          className={contentTab === 'brand' ? 'active' : ''}
          onClick={() => runWithEditorGuard(() => setContentTab('brand'))}
        >
          Marca y activos
        </button>
      </div>

      {contentTab === 'editor' ? (
        <div className="provi-content-editor-panel" role="tabpanel">
          <AdminVisualEditorLauncher
            embedded
            autoOpen
            onDirtyChange={setEditorDirty}
            onClose={() => {
              setEditorDirty(false);
              navigateToPage('dashboard');
            }}
          />
        </div>
      ) : (
        <div className="provi-content-brand-panel" role="tabpanel">
          <Panel title="🎯 Marca PROVI" subtitle="Nombre, logo, colores, soporte y ubicaciones." action={<Button onClick={() => showToast('Marca guardada')}>Guardar marca</Button>}>
        <div className="provi-form-grid">
          <Field label="Nombre público"><input className="provi-input" value={branding.company_name || 'PROVI'} onChange={(e) => setBranding((p) => ({ ...p, company_name: e.target.value }))} /></Field>
          <Field label="Nombre comercial"><input className="provi-input" value={branding.owner_name || ''} onChange={(e) => setBranding((p) => ({ ...p, owner_name: e.target.value }))} /></Field>
          <Field label="Correo de soporte"><input className="provi-input" value={branding.support_email || ''} onChange={(e) => setBranding((p) => ({ ...p, support_email: e.target.value }))} /></Field>
          <Field label="Logo"><div className="provi-empty"><ImageIcon size={18} /> Preview de logo {branding.logo_url ? 'cargado' : 'pendiente'}</div></Field>
        </div>
        <div className="provi-branding-section">
          <div className="provi-subsection-head">
            <div>
              <strong>🧩 Identidad visual / Logos</strong>
              <span>Configura logos, favicon e íconos de la app desde un solo lugar.</span>
            </div>
            <MiniButton onClick={() => saveBrandingVisualSettings()}>Guardar todo</MiniButton>
          </div>
          {renderBrandingVisualAssets()}
        </div>
        <div className="provi-actions" style={{ marginTop: 14 }}>
          <MiniButton tone="danger" onClick={() => askConfirm('Eliminar logo', 'Se quitará el logo de las superficies seleccionadas. ¿Continuar?', null, 'Logo eliminado')}>Eliminar logo</MiniButton>
        </div>
        <div className="provi-filterbar" style={{ marginTop: 14 }}>
          {['navbar', 'footer', 'login', 'register', 'emails', 'admin dashboard', 'provider dashboard', 'client dashboard'].map((item) => <Badge key={item} tone="purple">✓ {item}</Badge>)}
        </div>
          </Panel>
        </div>
      )}
    </section>
  );

  const renderSistema = () => (
    <div className="provi-grid-2">
      <Panel title="⚙️ Configuración general" subtitle="Sistema, módulos activos, mantenimiento y permisos." action={<Button tone="primary" onClick={() => askConfirm('Guardar configuración', 'Los cambios pueden afectar la plataforma. ¿Confirmas?', null, 'Configuración guardada')}>Guardar</Button>}>
        <div className="provi-form-grid">
          <Field label="Nombre de plataforma"><input className="provi-input" value="PROVI" readOnly data-testid="admin-settings-form" /></Field>
          <Field label="Moneda"><input className="provi-input" value="USD - Dólar estadounidense" readOnly /></Field>
          <Field label="País"><input className="provi-input" value="Ecuador" readOnly /></Field>
          <Field label="Zona horaria"><input className="provi-input" value="America/Guayaquil" readOnly /></Field>
        </div>
        <div style={{ marginTop: 16 }}>
          <SwitchRow title="Modo mantenimiento" text="Bloquea acceso público durante actualizaciones." />
          <SwitchRow title="Módulo Taxi" text="Permite solicitudes y viajes." active />
          <SwitchRow title="Módulo Comida" text="Permite restaurantes, menús y pedidos." active />
          <SwitchRow title="Módulo Delivery" text="Preparado para activar cuando esté listo." />
        </div>
      </Panel>
      <Panel title="🛡️ Sistema / Avanzado" subtitle="Solo superadmin: auditoría, versiones, diagnóstico, notificaciones y demo.">
        <div className="provi-list">
          <RowCard icon={Mail} title="Notificaciones" subtitle="Plantillas de correo y pruebas de envío." badge={`${visibleTemplates.length}`} badgeTone="blue" actions={<MiniButton onClick={() => goTo('notificaciones')}>Abrir</MiniButton>} />
          <RowCard icon={History} title="Historial de versiones" subtitle="Deploys, commits y restauración sin tocar datos." badge="OK" badgeTone="blue" />
          <RowCard icon={Activity} title="Auditoría" subtitle="Quién hizo qué, cuándo y desde dónde." badge="Local" badgeTone="purple" actions={<MiniButton onClick={() => setShowAudit(true)}>Ver auditoría</MiniButton>} />
          <RowCard icon={Wrench} title="Diagnóstico técnico" subtitle="Backend, frontend, base de datos, VPS y latencia." badge="Superadmin" badgeTone="green" />
          <RowCard icon={Sparkles} title="Demo" subtitle="Oculto de navegación principal. Solo pruebas controladas." badge="Oculto" badgeTone="red" />
        </div>
      </Panel>
    </div>
  );

  const renderNotifications = () => (
    <AdminEmailCenter onToast={showToast} />
  );

  const renderPage = () => {
    switch (activePage) {
      case 'operaciones': return renderOperaciones();
      case 'proveedores': return renderProveedores();
      case 'clientes': return renderClientes();
      case 'aprobaciones': return renderAprobaciones();
      case 'finanzas': return renderFinanzas();
      case 'soporte': return renderSoporte();
      case 'reportes': return renderReportes();
      case 'contenido': return renderContenido();
      case 'notificaciones': return renderNotifications();
      case 'sistema': return renderSistema();
      default: return renderDashboard();
    }
  };

  const adminLogoUrl = brandingVisual.admin_logo_url || brandingVisual.logo_icon_url;
  const displayedFontScale = showUiPreferences
    ? preferenceDraft.font_scale
    : uiPreferences.font_scale;

  return (
    <div
      className="provi-admin"
      data-font-scale={displayedFontScale}
      style={{
        '--admin-font-size':
          `${(displayedFontScale * 0.15).toFixed(2)}px`
      }}
      data-testid="admin-dashboard"
    >
      <div className={cn('provi-admin-shell', uiPreferences.sidebar_mode === 'compact' && 'sidebar-compact', temporarySidebarOpen && 'sidebar-temporary-open')}>
        <div className={cn('provi-sidebar-overlay', temporarySidebarOpen && 'show')} onClick={() => { if (!criticalModalOpen) setTemporarySidebarOpen(false); }} />
        <aside className="provi-sidebar" onMouseEnter={() => setSidebarHovering(true)} onMouseLeave={() => setSidebarHovering(false)} onFocusCapture={() => setSidebarFocusWithin(true)} onBlurCapture={(event) => { if (!event.currentTarget.contains(event.relatedTarget)) setSidebarFocusWithin(false); }}>
          <div className="provi-sidebar-head">
            <div className="provi-brand"><div className="provi-logo">{adminLogoUrl ? <img src={adminLogoUrl} alt="PROVI" /> : 'P'}</div><div><h1>PROVI</h1><span>Panel Administrador</span></div></div>
            <button className="provi-menu-button provi-sidebar-menu-button" onClick={toggleSidebar} aria-label={temporarySidebarOpen ? 'Cerrar barra lateral' : uiPreferences.sidebar_mode === 'compact' ? 'Abrir barra lateral' : 'Compactar barra lateral'} aria-expanded={temporarySidebarOpen || uiPreferences.sidebar_mode === 'expanded'}><Menu size={21} /></button>
          </div>
          <div className="provi-role-box"><label>Rol de vista</label><select className="provi-select" value={roleView} onChange={(e) => { setRoleView(e.target.value); addAudit('Permisos', `Cambió vista a ${roleLabels[e.target.value]}`); }}>
            {Object.entries(roleLabels).map(([key, label]) => <option key={key} value={key}>{label}</option>)}
          </select></div>
          <div className="provi-sidebar-navigation">
            {navGroups.map((group) => <div key={group.title}><div className="provi-group-title">{group.title}</div><nav className="provi-nav" aria-label={group.title}>{group.items.filter((item) => allowedPages.includes(item.id)).map(({ id, label, icon: Icon, count }) => <button key={id} title={label} aria-label={label} aria-current={activePage === id ? 'page' : undefined} onClick={() => goTo(id)} className={cn('provi-nav-item', activePage === id && 'active')}><Icon size={19} /><span>{label}</span>{count ? <small>{count}</small> : null}</button>)}</nav></div>)}
          </div>
          <button
            type="button"
            className="provi-sidebar-settings-button"
            title="Ajustes de interfaz"
            aria-label="Abrir ajustes de interfaz"
            onClick={() => {
              setPreferenceDraft(uiPreferences);
              setTemporarySidebarOpen(false);
              setShowUiPreferences(true);
            }}
          >
            <Settings size={18} />
            <span>Ajustes de interfaz</span>
          </button>
        </aside>
        <main className="provi-main">
          <header className="provi-topbar">
            <button className="provi-menu-button provi-mobile-menu-button" onClick={toggleSidebar} aria-label={temporarySidebarOpen ? 'Cerrar barra lateral' : uiPreferences.sidebar_mode === 'compact' ? 'Abrir barra lateral' : 'Compactar barra lateral'} aria-expanded={temporarySidebarOpen || uiPreferences.sidebar_mode === 'expanded'}><Menu size={21} /></button>
            <div className="provi-title"><h2>{currentTitle[0]}</h2><p>{currentTitle[1]}</p></div>
            <div className="provi-search-wrap"><input className="provi-search" placeholder="Buscar cliente, proveedor, viaje, pedido o ticket..." onKeyDown={(e) => { if (e.key === 'Enter' && e.currentTarget.value.trim()) setModal({ title: 'Resultados de búsqueda', text: `Resultados simulados para: ${e.currentTarget.value.trim()}` }); }} /><Search className="provi-search-icon" size={18} /></div>
            <div className="provi-top-actions"><span className="provi-user-pill">{roleLabels[roleView]} · {user?.email}</span><Button onClick={() => setShowAudit(true)}><History size={16} />Auditoría</Button><Button onClick={handleLogout} data-testid="auth-logout-button"><LogOut size={16} />Salir</Button></div>
          </header>
          <div className="provi-page-scroll">
            {renderPage()}
          </div>
        </main>
      </div>
      {showUiPreferences && (
        <div
          className="provi-modal-backdrop show"
          onMouseDown={(event) => {
            if (
              event.target === event.currentTarget
              && !savingPreferences
            ) {
              setPreferenceDraft(uiPreferences);
              setShowUiPreferences(false);
            }
          }}
        >
          <section
            className="provi-interface-settings"
            role="dialog"
            aria-modal="true"
            aria-labelledby="provi-interface-settings-title"
          >
            <header className="provi-interface-settings-head">
              <div>
                <h3 id="provi-interface-settings-title">
                  Ajustes de interfaz
                </h3>
                <p>
                  Estos cambios se guardan únicamente
                  para tu usuario administrador.
                </p>
              </div>
              <button
                type="button"
                className="provi-close"
                disabled={savingPreferences}
                aria-label="Cerrar ajustes"
                onClick={() => {
                  setPreferenceDraft(uiPreferences);
                  setShowUiPreferences(false);
                }}
              >
                <X size={18} />
              </button>
            </header>

            <div className="provi-interface-setting-block">
              <div className="provi-interface-setting-title">
                <div>
                  <strong>Tamaño del texto</strong>
                  <span>
                    Ajusta todos los textos del panel.
                  </span>
                </div>
                <output>
                  {preferenceDraft.font_scale}%
                </output>
              </div>

              <div className="provi-font-range-row">
                <span aria-hidden="true">A−</span>
                <input
                  className="provi-font-range"
                  type="range"
                  min="90"
                  max="115"
                  step="1"
                  value={preferenceDraft.font_scale}
                  aria-label="Tamaño del texto"
                  aria-valuetext={`${preferenceDraft.font_scale}%`}
                  onChange={(event) => {
                    setPreferenceDraft((current) => ({
                      ...current,
                      font_scale: Number(
                        event.target.value
                      )
                    }));
                  }}
                />
                <span aria-hidden="true">A+</span>
              </div>

              <small>
                Tamaño aproximado:
                {' '}
                {(
                  preferenceDraft.font_scale
                  * 0.15
                ).toFixed(1)}
                {' px'}
              </small>
            </div>

            <div className="provi-interface-setting-block">
              <div className="provi-interface-setting-title">
                <div>
                  <strong>Barra lateral</strong>
                  <span>
                    Elige cuánto espacio ocupa.
                  </span>
                </div>
              </div>

              <div className="provi-segmented">
                {[
                  ['expanded', 'Completa'],
                  ['compact', 'Compacta']
                ].map(([value, label]) => (
                  <button
                    type="button"
                    key={value}
                    className={
                      preferenceDraft.sidebar_mode
                      === value
                        ? 'active'
                        : ''
                    }
                    onClick={() => {
                      setPreferenceDraft(
                        (current) => ({
                          ...current,
                          sidebar_mode: value
                        })
                      );
                    }}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>

            <footer className="provi-interface-settings-actions">
              <Button
                disabled={savingPreferences}
                onClick={() => {
                  setPreferenceDraft(uiPreferences);
                  setShowUiPreferences(false);
                }}
              >
                Cancelar
              </Button>

              <Button
                tone="primary"
                disabled={savingPreferences}
                onClick={async () => {
                  const saved =
                    await saveUiPreferences();

                  if (saved) {
                    setShowUiPreferences(false);
                  }
                }}
              >
                {savingPreferences
                  ? 'Guardando…'
                  : 'Guardar cambios'}
              </Button>
            </footer>
          </section>
        </div>
      )}

      <Modal modal={modal} setModal={setModal} />
      <ConfirmModal confirm={confirm} setConfirm={setConfirm} />
      <EditorNavigationGuard
        pendingAction={pendingEditorAction}
        onKeepEditing={() => setPendingEditorAction(null)}
        onLeave={() => {
          const action = pendingEditorAction?.run;
          setPendingEditorAction(null);
          setEditorDirty(false);
          action?.();
        }}
      />
      <AuditModal open={showAudit} setOpen={setShowAudit} audit={audit} />
      <div className={cn('provi-toast', toast && 'show')}>{toast}</div>
    </div>
  );
};

const Field = ({ label, children }) => <label className="provi-field"><span>{label}</span>{children}</label>;

const ModuleCard = ({ icon: Icon, title, status, statusTone = 'green', text, actions }) => (
  <article className="provi-card">
    <div className="provi-card-head"><h3>{title}</h3><Badge tone={statusTone}>{status}</Badge></div>
    <p>{text}</p>
    <div className="provi-card-actions">{actions}</div>
  </article>
);

const SwitchRow = ({ title, text, active = false }) => {
  const [on, setOn] = useState(active);
  return (
    <div className="provi-switch-row">
      <div><strong>{title}</strong><br /><span className="provi-muted">{text}</span></div>
      <button className={cn('provi-switch', on && 'on')} onClick={() => setOn((value) => !value)}><div className="provi-switch-dot" /></button>
    </div>
  );
};

const Modal = ({ modal, setModal }) => {
  if (!modal) return null;
  return (
    <div
      className="provi-modal-backdrop show"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          setModal(null);
        }
      }}
    >
      <div className={cn('provi-modal', modal.wide && 'provi-modal-wide')}>
        <div className="provi-modal-head"><div><h3>{modal.title}</h3>{modal.text && <p>{modal.text}</p>}</div><button
          type="button"
          className="provi-close"
          aria-label="Cerrar ventana"
          onClick={() => setModal(null)}
        >
          <X size={18} />
        </button></div>
        {modal.custom || <textarea className="provi-textarea" placeholder="Nota administrativa..." />}
        <div className="provi-actions" style={{ marginTop: 16 }}><Button onClick={() => setModal(null)}>Cancelar</Button><Button tone="primary" onClick={() => setModal(null)}>Guardar</Button></div>
      </div>
    </div>
  );
};

const ConfirmModal = ({ confirm, setConfirm }) => {
  if (!confirm) return null;
  return (
    <div
      className="provi-modal-backdrop show"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          setConfirm(null);
        }
      }}
    >
      <div className="provi-modal" style={{ maxWidth: 520 }}>
        <div className="provi-modal-head"><div><h3>{confirm.title}</h3><p>{confirm.text}</p></div><button
          type="button"
          className="provi-close"
          aria-label="Cerrar confirmación"
          onClick={() => setConfirm(null)}
        >
          <X size={18} />
        </button></div>
        <div className="provi-empty">Esta acción quedará registrada en auditoría. Sí, porque luego nadie recuerda quién “solo tocó un botoncito”.</div>
        <div className="provi-actions" style={{ marginTop: 16 }}><Button onClick={() => setConfirm(null)}>Cancelar</Button><Button tone="danger" onClick={confirm.action}>Confirmar</Button></div>
      </div>
    </div>
  );
};

const EditorNavigationGuard = ({ pendingAction, onKeepEditing, onLeave }) => {
  if (!pendingAction) return null;

  return (
    <div className="provi-modal-backdrop show">
      <section
        className="provi-modal"
        role="dialog"
        aria-modal="true"
        aria-labelledby="provi-editor-navigation-title"
        style={{ maxWidth: 520 }}
      >
        <div className="provi-modal-head">
          <div>
            <h3 id="provi-editor-navigation-title">Cambios sin guardar</h3>
            <p>Hay cambios sin guardar. Si sales ahora se perderán.</p>
          </div>
        </div>
        <div className="provi-actions" style={{ marginTop: 16 }}>
          <Button onClick={onKeepEditing}>Continuar editando</Button>
          <Button tone="danger" onClick={onLeave}>Salir sin guardar</Button>
        </div>
      </section>
    </div>
  );
};

const AuditModal = ({ open, setOpen, audit }) => {
  if (!open) return null;
  return (
    <div
      className="provi-modal-backdrop show"
      onClick={(event) => {
        if (event.target === event.currentTarget) {
          setOpen(false);
        }
      }}
    >
      <div className="provi-modal">
        <div className="provi-modal-head"><div><h3>📜 Centro de Auditoría</h3><p>Registro visual de acciones administrativas.</p></div><button
          type="button"
          className="provi-close"
          aria-label="Cerrar auditoría"
          onClick={() => setOpen(false)}
        >
          <X size={18} />
        </button></div>
        <div className="provi-list">{audit.map((item, index) => <div key={`${item.type}-${index}`} className="provi-row-card"><strong>{item.type}:</strong> <span className="provi-muted">{item.text} · {item.at}</span></div>)}</div>
      </div>
    </div>
  );
};

export default AdminCommandCenter;
